import {
  CREATE_PRB_REVIEW,
  DELETE_PRB_REVIEW,
  FETCH_PRB_REVIEW_BY_ID,
  FETCH_PRB_REVIEWS,
  FETCH_PRB_REVIEWS_BY_CATALOG,
  SEARCH_HUB_CATALOGS,
  SEARCH_PRB_REVIEWS,
} from "@apiUtils";
import {
  mutationFetch,
  type SearchPayload,
  type UseSearchQueryOptions,
  useSearchQuery,
} from "@atlas/sdk";
import type { IHubReview } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import {
  getHubCatalogByIdKey,
  HUB_CATALOG_BY_ID_AND_VERSION_KEY,
  HUB_CATALOG_BY_ID_KEY,
  HUB_CATALOGS_BY_TYPE_KEY,
  HUB_CATALOGS_KEY,
} from "./HubCatalogs";

export const HUB_REVIEWS_KEY = "hubReviewsKey";
export const HUB_REVIEW_BY_ID_KEY = "hubReviewByIdKey";
export const HUB_REVIEWS_BY_CATALOG_KEY = "hubReviewsByCatalogKey";

export const getHubReviewsKey = () => [HUB_REVIEWS_KEY] as const;

export const getHubReviewByIdKey = (reviewId: number) =>
  [HUB_REVIEW_BY_ID_KEY, reviewId] as const;

export const getHubReviewsByCatalogKey = (catalogId: number) =>
  [HUB_REVIEWS_BY_CATALOG_KEY, catalogId] as const;

export const useHubReviews = (
  options?: Partial<
    UseQueryOptions<
      IHubReview[],
      AxiosError,
      IHubReview[],
      ReturnType<typeof getHubReviewsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubReviewsKey(),
    queryFn: async () => {
      const rows = await mutationFetch<IHubReview[]>({
        url: FETCH_PRB_REVIEWS,
        method: "GET",
      });
      return rows;
    },
    ...options,
  });
};

export const useHubReview = (
  reviewId: number,
  options?: Partial<
    UseQueryOptions<
      IHubReview,
      AxiosError,
      IHubReview,
      ReturnType<typeof getHubReviewByIdKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubReviewByIdKey(reviewId),
    queryFn: async () => {
      const row = await mutationFetch<IHubReview>({
        url: FETCH_PRB_REVIEW_BY_ID.replace(":reviewId", String(reviewId)),
        method: "GET",
      });
      return row;
    },
    ...options,
    enabled: (options?.enabled ?? true) && reviewId > 0,
  });
};

export const useHubReviewsByCatalog = (
  catalogId: number,
  options?: Partial<
    UseQueryOptions<
      IHubReview[],
      AxiosError,
      IHubReview[],
      ReturnType<typeof getHubReviewsByCatalogKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubReviewsByCatalogKey(catalogId),
    queryFn: async () => {
      const rows = await mutationFetch<IHubReview[]>({
        url: FETCH_PRB_REVIEWS_BY_CATALOG.replace(
          ":catalogId",
          String(catalogId),
        ),
        method: "GET",
      });
      return rows;
    },
    ...options,
    enabled: (options?.enabled ?? true) && catalogId > 0,
  });
};

const createHubReview = (body: IHubReview): Promise<IHubReview> => {
  return mutationFetch<IHubReview>({
    url: CREATE_PRB_REVIEW,
    method: "POST",
    body,
  });
};

const deleteHubReview = (reviewId: number): Promise<void> => {
  return mutationFetch<void>({
    url: DELETE_PRB_REVIEW.replace(":reviewId", String(reviewId)),
    method: "DELETE",
  });
};

export const useCreateHubReview = (
  options?: UseMutationOptions<IHubReview, AxiosError, IHubReview>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHubReview,
    onSuccess: (data: IHubReview) => {
      queryClient.invalidateQueries({ queryKey: [HUB_REVIEWS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_PRB_REVIEWS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_BY_TYPE_KEY] });
      if (data.reviewId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubReviewByIdKey(data.reviewId),
        });
      }
      if (data.catalogId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubReviewsByCatalogKey(data.catalogId),
        });
        queryClient.invalidateQueries({
          queryKey: getHubCatalogByIdKey(data.catalogId),
        });
        queryClient.invalidateQueries({
          queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY, data.catalogId],
        });
      }
    },
    ...options,
  });
};

export const useDeleteHubReview = (
  options?: UseMutationOptions<void, AxiosError, number>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHubReview,
    onSuccess: (_, reviewId: number) => {
      queryClient.invalidateQueries({ queryKey: [HUB_REVIEWS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_PRB_REVIEWS] });
      queryClient.invalidateQueries({
        queryKey: getHubReviewByIdKey(reviewId),
      });
      queryClient.invalidateQueries({
        queryKey: [HUB_REVIEWS_BY_CATALOG_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_BY_TYPE_KEY] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_BY_ID_KEY] });
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY],
      });
    },
    ...options,
  });
};

export const useSearchHubReviews = (
  payload: SearchPayload,
  options?: Omit<UseSearchQueryOptions<IHubReview>, "url" | "payload">,
) => {
  return useSearchQuery<IHubReview>({
    url: SEARCH_PRB_REVIEWS,
    payload,
    ...options,
  });
};
